import React, { useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";

const IMPORTS = [["items", "Items / products"], ["customers", "Customers"], ["suppliers", "Suppliers"]];
const EXPORTS = [["items", "Items"], ["customers", "Customers"], ["suppliers", "Suppliers"], ["sales", "Sale bills"], ["purchases", "Purchase bills"], ["expenses", "Expenses"]];

export default function ExcelPanel({ qs = "", onChanged }) {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const refs = { items: useRef(), customers: useRef(), suppliers: useRef() };
  const amp = qs ? "&" : "?";

  const upload = async (kind, f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f); setBusy(kind); setResult(null);
    try {
      const { data } = await api.post(`/excel/import/${kind}${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ kind, ...data }); toast.success(`Imported ${kind}`, { description: `${data.created} new · ${data.updated} updated` }); onChanged?.();
    } catch (e) { toast.error("Import failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(""); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="excel-panel">
      <section className="bg-white border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center gap-2"><Upload size={15} /><h3 className="font-heading font-bold">Import from Excel / Vyapar</h3></div>
        <p className="text-xs text-zinc-500">Upload the .xlsx exported from Vyapar or filled from our template. Column names are auto-detected (Item Name, Item Code, HSN, Sale Price, Purchase Price, Tax Rate, Stock Quantity, Min Stock · Party Name, Phone, GSTIN, State, Address). Existing names/barcodes are updated, not duplicated.</p>
        <div className="divide-y divide-zinc-100">
          {IMPORTS.map(([k, l]) => (
            <div key={k} className="flex items-center gap-2 py-2" data-testid={`import-row-${k}`}>
              <span className="text-sm font-medium flex-1">{l}</span>
              <a data-testid={`template-${k}`} href={`${API_BASE}/excel/template/${k}${qs}`} className="text-[10px] tracking-[0.14em] uppercase font-semibold px-2 py-1.5 border border-zinc-300 hover:bg-zinc-100">Template</a>
              <input ref={refs[k]} type="file" accept=".xlsx" className="hidden" data-testid={`import-file-${k}`} onChange={(e) => { upload(k, e.target.files?.[0]); e.target.value = ""; }} />
              <button data-testid={`import-btn-${k}`} disabled={busy === k} onClick={() => refs[k].current?.click()} className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-50">{busy === k ? "Importing…" : "Upload .xlsx"}</button>
            </div>
          ))}
        </div>
        {result ? <div data-testid="import-result" className="text-xs border border-emerald-200 bg-emerald-50 p-3"><b>{result.kind}</b>: {result.rows} rows · {result.created} created · {result.updated} updated{result.errors?.length ? <ul className="mt-1 text-rose-700 list-disc pl-4">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul> : null}</div> : null}
      </section>
      <section className="bg-white border border-zinc-200 p-4 space-y-3">
        <div className="flex items-center gap-2"><Download size={15} /><h3 className="font-heading font-bold">Export to Excel</h3></div>
        <p className="text-xs text-zinc-500">Download your data as .xlsx — share with your accountant or import into Vyapar / Tally.</p>
        <div className="grid grid-cols-2 gap-2">
          {EXPORTS.map(([k, l]) => <a key={k} data-testid={`export-${k}`} href={`${API_BASE}/excel/export/${k}${qs}`} className="flex items-center gap-2 border border-zinc-200 px-3 py-2.5 text-sm hover:bg-zinc-900 hover:text-white transition-colors"><FileSpreadsheet size={14} /> {l}</a>)}
        </div>
        <div className="text-[11px] text-zinc-400">{amp === "?" ? "Exports include all records." : "Exports include all records of this client."}</div>
      </section>
    </div>
  );
}
